import React, { useState } from 'react';
import './Services.css';
import IconBadge from './IconBadge';
import SectionCTA from './SectionCTA';
import useScrollReveal from '../hooks/useScrollReveal';

const WorkflowCard = () => (
  <div className="service-card-ui workflow-ui">
    <div className="ui-header">
      <span className="ui-label">All Tasks</span>
      <span className="ui-badge">Waiting for approval</span>
    </div>
    <div className="task-list">
      {[
        { name: 'Payroll management', meta: 'Due on 2nd July', color: '#5ECBA1' },
        { name: 'Employee Tracking', meta: '2 days ago', color: '#a78bfa' },
        { name: 'Social media post', meta: 'Cancelled by user', color: '#f87171' },
        { name: 'Lead list', meta: '70% prepared', color: '#60a5fa' },
        { name: 'Payment reminder', meta: 'Sent to selected clients', color: '#fbbf24' },
      ].map((t, i) => (
        <div className="task-item" key={i}>
          <div className="task-dot" style={{ background: t.color }} />
          <div>
            <div className="task-name">{t.name}</div>
            <div className="task-meta">{t.meta}</div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const AIAssistantCard = () => (
  <div className="service-card-ui ai-ui">
    <div className="ai-prompt">
      <div className="ai-prompt-text">What can I help with?</div>
      <p className="ai-prompt-sub">Whether you want help in customer handling or make changes in your system just give me a command</p>
      <div className="ai-input-bar">
        <div className="ai-cursor" />
        <span className="ai-input-placeholder">Ask anything...</span>
      </div>
    </div>
    <div className="ai-chips">
      {['Add document', 'Analyze', 'Generate Image', 'Research'].map((c, i) => (
        <span key={i} className="ai-chip">{c}</span>
      ))}
    </div>
  </div>
);

const EmailCard = () => {
  const [activeTab, setActiveTab] = useState(0);
  const tabs = ['Draft', 'Schedule', 'Sent'];
  const contacts = [
    { name: 'Jack Daniel', role: 'Founder', email: 'justin@main.com', company: 'Xavier LLC' },
    { name: 'Gorge Chapel', role: 'Founder', email: 'gorge@mail.com', company: 'Chapel LLC' },
    { name: 'Mike Tylor', role: 'Founder', email: 'mike@Cmb.com', company: 'CMB LLC' },
  ];
  return (
    <div className="service-card-ui email-ui">
      <div className="email-header">
        <span className="email-sending">E-mail Sending..</span>
        <div className="email-filters">
          {['LinkedIn', 'IT services', 'Founders'].map((f, i) => (
            <span key={i} className={`email-filter ${i === 2 ? 'active' : ''}`}>{f}</span>
          ))}
        </div>
      </div>
      <div className="email-contacts">
        {contacts.map((c, i) => (
          <div className="email-contact" key={i}>
            <div className="contact-avatar">{c.name[0]}</div>
            <div className="contact-info">
              <div className="contact-name">{c.name} <span className="contact-role">{c.role}</span></div>
              <div className="contact-email">{c.email}</div>
            </div>
            <div className="contact-verified"><IconBadge name="verified" size="sm" /></div>
          </div>
        ))}
      </div>
      <div className="email-tabs">
        {tabs.map((t, i) => (
          <button key={i} className={`email-tab ${activeTab === i ? 'active' : ''}`} onClick={() => setActiveTab(i)}>{t}</button>
        ))}
      </div>
    </div>
  );
};

const CustomProjectCard = () => (
  <div className="service-card-ui custom-ui">
    <div className="custom-greeting">
      <IconBadge name="portfolio" size="lg" className="custom-wave" />
      <div>
        <div className="custom-hey">Hey David!</div>
        <div className="custom-sub">Here is your Custom project & schedule</div>
      </div>
    </div>
    <div className="custom-project">
      <div className="project-label">On going project:</div>
      <div className="project-name">Customer Support Chatbot</div>
      <div className="project-progress">
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: '90%' }} />
        </div>
        <span>90% Finished</span>
      </div>
    </div>
    <div className="custom-schedule">
      <div className="schedule-label">Schedule</div>
      <div className="schedule-days">
        {['Mo','Tu','We','Th','Fr','Sa','Su'].map((d, i) => (
          <span key={i} className={`sday ${i === 0 || i === 2 ? 'active' : ''}`}>{d}</span>
        ))}
      </div>
      <div className="schedule-items">
        <div className="schedule-item">
          <div className="si-time">10:00 am – 10:30 am</div>
          <div className="si-name">Discovery call</div>
        </div>
        <div className="schedule-item">
          <div className="si-time">06:00 pm – 06:30 pm</div>
          <div className="si-name">Custom automation</div>
        </div>
      </div>
    </div>
  </div>
);

const services = [
  {
    id: 'workflow',
    title: 'Workflow Automation',
    heading: 'Automate Repetitive Work',
    desc: 'We first structure and digitalize the workflow, then automate repetitive execution, then add AI where decisions and speed matter most.',
    tags: ['Internal Task Bots', '40+ Automations'],
    card: <WorkflowCard />,
  },
  {
    id: 'ai',
    title: 'AI Assistant',
    heading: 'Smart Workflow Assistants',
    desc: 'Let AI handle emails, summaries, follow-ups, and scheduling—so you reclaim your day.',
    tags: ['Summaries', 'Scheduling', 'Many more'],
    card: <AIAssistantCard />,
  },
  {
    id: 'sales',
    title: 'Sales & Marketing',
    heading: 'Boost Growth',
    desc: 'Use AI for smarter lead handling, automated outreach, and data-backed insights that convert faster.',
    tags: ['Leads', 'Content', 'Social post'],
    card: <EmailCard />,
  },
  {
    id: 'custom',
    title: 'Custom Projects',
    heading: 'Custom AI Builds',
    desc: 'Need a unique tool? We consult, design, and build bespoke AI systems shaped to your workflow needs.',
    tags: ['Strategy', 'Custom AI', 'Consulting'],
    card: <CustomProjectCard />,
  },
];

export default function Services() {
  const headerRef = useScrollReveal();
  return (
    <section className="services" id="services">
      <div className="services-inner">
        <div className="services-header reveal" ref={headerRef}>
          <span className="section-label">Our Services</span>
          <h2 className="section-title">Tailored AI Services<br />That Work Smarter</h2>
          <p className="services-sub">
            From audit to pilot to full-scale rollout, we build tools that optimize workflows and reduce manual effort—without adding headcount.
          </p>
        </div>
        <div className="services-grid">
          {services.map((s) => (
            <div className="service-item" key={s.id}>
              <div className="service-content">
                <div className="service-tag-label">{s.title}</div>
                <h3 className="service-heading">{s.heading}</h3>
                <p className="service-desc">{s.desc}</p>
                <div className="service-tags">
                  {s.tags.map((t, i) => (
                    <span key={i} className="service-chip">{t}</span>
                  ))}
                </div>
              </div>
              <div className="service-card-wrap">
                {s.card}
              </div>
            </div>
          ))}
        </div>
        <SectionCTA label="See a workflow that looks familiar?" />
      </div>
    </section>
  );
}
